import { createFileRoute, notFound } from "@tanstack/react-router";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { FaqSourceNote } from "@/components/FaqSourceNote";
import { pageHeadForProvince, getActiveSiteUrl } from "@/lib/seo-helpers";
import { buildBreadcrumbSchema, buildFaqSchema, buildSpeakableSchema } from "@/lib/schema";
import { VISA_FAQ_CATEGORIES, getVisaCategoryDef } from "@/content/visa-corpus/faq-defs";

export const Route = createFileRoute("/en/visa/faq/$category")({
  loader: async ({ params }) => {
    const def = getVisaCategoryDef(params.category);
    if (!def) throw notFound();
    const { getVisaFaqsByCategoryEn } = await import("@/content/visa-corpus");
    return { def, faqs: getVisaFaqsByCategoryEn(def.slug) };
  },
  component: EnVisaFaqCategoryPage,
  head: ({ params, loaderData }) => {
    if (!loaderData) {
      return { meta: [{ title: "Category not found" }, { name: "robots", content: "noindex" }] };
    }
    const site = getActiveSiteUrl();
    const { def, faqs } = loaderData;
    const title = `${def.titleEn} — Thailand Visa FAQ (${faqs.length} answers)`;
    return {
      ...pageHeadForProvince({
        title,
        description: def.descEn,
        path: `/en/visa/faq/${params.category}`,
        type: "article",
        locale: "en_US",
        alternates: [{ lang: "th", path: `/visa/faq/${params.category}` }],
      }),
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify(
            buildBreadcrumbSchema([
              { name: "Home", url: site },
              { name: "Thailand Visa & Immigration FAQ", url: `${site}/en/visa/faq` },
              { name: def.titleEn, url: `${site}/en/visa/faq/${params.category}` },
            ]),
          ),
        },
        { type: "application/ld+json", children: JSON.stringify(buildFaqSchema(faqs)) },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebPage",
            speakable: { "@type": "SpeakableSpecification", cssSelector: ["h1", "h2", ".faq-answer"] },
          }),
        },
        { type: "application/ld+json", children: JSON.stringify(buildSpeakableSchema(`/en/visa/faq/${params.category}`)) },
      ],
    };
  },
});

function EnVisaFaqCategoryPage() {
  const { def, faqs } = Route.useLoaderData();
  return (
    <>
      <SiteHeader />
      <main className="container mx-auto max-w-4xl px-4 py-10">
        <nav aria-label="breadcrumb" className="mb-4 text-sm text-muted-foreground">
          <a href="/en" className="hover:underline">Home</a> <span>/</span>{" "}
          <a href="/en/visa/faq" className="hover:underline">Visa FAQ</a> <span>/</span>{" "}
          <span>{def.titleEn}</span>
        </nav>
        <h1 className="text-3xl md:text-4xl font-bold mb-3">{def.titleEn}</h1>
        <p className="text-muted-foreground mb-6">{def.descEn}</p>
        <p className="text-sm text-muted-foreground mb-8">
          <strong>{faqs.length}</strong> questions in this topic, based on published guidance from the Immigration
          Bureau, Department of Employment and Ministry of Foreign Affairs. All approvals remain at the discretion
          of the deciding officer.
        </p>

        <div className="mb-8 flex flex-wrap gap-2 text-xs">
          {VISA_FAQ_CATEGORIES.map((c) => (
            <a
              key={c.slug}
              href={`/en/visa/faq/${c.slug}`}
              className={`rounded-full border px-3 py-1 ${c.slug === def.slug ? "bg-primary text-primary-foreground" : "bg-muted/40 text-muted-foreground hover:bg-muted"}`}
            >
              {c.titleEn}
            </a>
          ))}
        </div>

        <div className="space-y-4">
          {faqs.map((item: { q: string; a: string }, i: number) => (
            <details key={i} className="rounded-lg border bg-card p-4 open:shadow-sm" {...(i < 5 ? { open: true } : {})}>
              <summary className="cursor-pointer font-semibold">
                <span className="text-muted-foreground mr-2">Q{i + 1}.</span>
                {item.q}
              </summary>
              <p className="faq-answer mt-3 text-sm leading-relaxed text-muted-foreground">{item.a}</p>
            </details>
          ))}
          {faqs.length === 0 && (
            <p className="rounded-lg border bg-muted/30 p-6 text-sm text-muted-foreground">
              No questions in this topic yet — <a href="/contact" className="text-primary underline">ask our team</a>.
            </p>
          )}
        </div>

        <section className="mt-12 rounded-lg border p-6 bg-primary/5">
          <h2 className="text-xl font-bold mb-2">Talk to our visa &amp; documents team</h2>
          <p className="text-sm text-muted-foreground mb-4">Mon–Sat 9:00–18:00 (ICT)</p>
          <a href="/contact" className="inline-block rounded-md bg-primary px-5 py-2 text-primary-foreground font-medium">Contact our team →</a>
        </section>

        <FaqSourceNote topic="visa" locale="en" />
      </main>
      <SiteFooter />
    </>
  );
}